/**
 * Melodic dictation — "Listen, then reproduce the melody on the input
 * surface." The first "produce, not just recognize" exercise type.
 *
 * Audio plan: cadence to establish the key, then a 3–8-note melody
 * played in even rhythm in the reference octave above the tonic. The
 * user reproduces the melody via `<DictationInput>` (solfège buttons,
 * piano keyboard, or MIDI).
 *
 * Generation algorithm: walk the scale degrees with a step-bias —
 * neighbor moves are most common, leaps proportionally less common,
 * and the melody is constrained to the configured `range`. The first
 * note is one of degrees 1, 3, or 5 (a stable start). Chromatic mode
 * sprinkles ±1-semitone alterations on the historically musical
 * neighbor degrees (♯4, ♭6) — never on the tonic, dominant, or third
 * (those resolve too strongly to bear chromatic alteration without
 * sounding mannered).
 *
 * Spec §8.1 puts the length range at 3..8; this generator clamps to
 * that. `range: "narrow"` constrains adjacent moves to a 5th and the
 * total melody to within a 5th of the start; `range: "wide"` allows
 * up to an octave both per-step and overall.
 *
 * `expectedSequence` is populated on `meta` so the grader and the audio
 * plan are guaranteed to match — the grader compares to the same
 * sequence the user heard.
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import { appendCadence, pickKey, MODE_PATTERNS, normalizeMode, type CadenceOptions, type KeyChoice } from "../utils.js";
import type { ScaleDegree, ScaleDegreeSequence } from "@music-app/theory-engine";

export interface MelodicDictationParams {
  /** 3..8, per spec §8.1. Values outside the range are clamped. */
  length: number;
  /** narrow = within a 5th; wide = up to an octave. */
  range: "narrow" | "wide";
  /** Allow chromatic neighbors (♯4, ♭6) in the generated melody. */
  chromatic: boolean;
}

/** Step weights — how often the walker chooses a step of N degrees up
 *  or down. Neighbor steps dominate; large leaps are rare. The same
 *  table is sampled for both up and down moves. */
const STEP_WEIGHTS: ReadonlyArray<{ step: number; weight: number }> = [
  { step: 1, weight: 5 },
  { step: 2, weight: 3 },
  { step: 3, weight: 2 },
  { step: 4, weight: 1 },
];

const MELODY_NOTE_DURATION = 0.6;
const MELODY_NOTE_GAP = 0.05;
const TEACH_NOTE_DURATION = 1.2; // ~2× — slow target playback per spec §8.1
const TEACH_NOTE_GAP = 0.1;

export function generateMelodicDictation(
  itemId: string,
  params: MelodicDictationParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);
  const length = clampLength(params.length);
  const key = pickKey(rng, "major");

  const sequence = buildSequence(rng, length, params.range, params.chromatic);

  // Audio plan: cadence + melody. The melody plays in the reference
  // octave (tonicMidi + 12) so the user hears it consistently above
  // the cadence and the grader's octave-displacement default keeps
  // the comparison stable.
  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  for (const sd of sequence) {
    const midi = toMidiInReferenceOctave(sd, key);
    builder.addNote(midi, MELODY_NOTE_DURATION, 0.22, "target");
    builder.advance(MELODY_NOTE_DURATION + MELODY_NOTE_GAP);
  }
  builder.advance(0.3);
  const audio = builder.build();

  // No multiple-choice options for dictation; the answer is a sequence.
  // Keep the array empty + correctAnswerId empty so accidental routes
  // through `gradeAnswer` are stable no-ops (the dispatcher in
  // Practice routes to `gradeDictation` instead).
  const options: AnswerOption[] = [];

  return {
    itemId,
    exerciseType: "melodic-dictation",
    audio,
    prompt: "Reproduce the melody.",
    options,
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: buildExplanation(sequence, key),
      distractorCount: 0,
      teachAudio: buildDictationTeachPlan(sequence, key),
      tonicMidi: key.tonicMidi,
      tonalityMode: "major",
      expectedSequence: sequence,
      chromaticEnabled: params.chromatic,
      range: params.range,
    },
  };
}

/* ─── Sequence construction ──────────────────────────────────────────── */

function clampLength(n: number): number {
  if (!Number.isFinite(n)) return 4;
  return Math.max(3, Math.min(8, Math.round(n)));
}

/** Diatonic step (1..7) → diatonic step that wraps around degrees. */
function wrapDegree(d: number): number {
  return ((d - 1) % 7 + 7) % 7 + 1;
}

/**
 * Walk the scale by sampled step deltas, biased toward neighbors.
 * Constraints:
 *   - Start on degree 1, 3, or 5 (stable scale tones).
 *   - "narrow": step ≤ 4 degrees (a 5th-ish), total melody within ±4
 *     degrees of the start.
 *   - "wide": step ≤ 7 degrees (an octave), no overall envelope cap.
 *   - Chromatic: ~25% chance to flip ♯4 or ♭6 when the chosen degree
 *     is 4 or 6, respectively. Never alters degrees 1/3/5 (too stable).
 */
function buildSequence(
  rng: Rng,
  length: number,
  range: "narrow" | "wide",
  chromatic: boolean,
): ScaleDegreeSequence {
  const startDegree = rng.pick([1, 3, 5]);
  const out: ScaleDegree[] = [{ degree: startDegree }];

  const maxStep = range === "narrow" ? 4 : 7;
  const envelope = range === "narrow" ? 4 : Infinity;

  // Track an abstract "scale-degree position" that includes octave
  // wraparound so the envelope check is meaningful (e.g. degree 1 in
  // octave 0 ≠ degree 1 in octave 1).
  let absPos = startDegree - 1; // 0-based, scale-step coordinates

  while (out.length < length) {
    const stepMag = sampleStep(rng, maxStep);
    const direction = rng.next() < 0.5 ? 1 : -1;
    const candidatePos = absPos + direction * stepMag;
    if (Math.abs(candidatePos - (startDegree - 1)) > envelope) {
      // Outside the envelope — try the opposite direction once.
      const flipped = absPos - direction * stepMag;
      if (Math.abs(flipped - (startDegree - 1)) > envelope) continue;
      absPos = flipped;
    } else {
      absPos = candidatePos;
    }

    const nextDegree = wrapDegree(absPos + 1);
    const sd: ScaleDegree = { degree: nextDegree };

    // Chromatic alteration — only on degrees 4 and 6, with ~25% chance.
    if (chromatic && (nextDegree === 4 || nextDegree === 6) && rng.next() < 0.25) {
      sd.alteration = nextDegree === 4 ? "♯" : "♭";
    }

    out.push(sd);
  }

  return out;
}

function sampleStep(rng: Rng, maxStep: number): number {
  const filtered = STEP_WEIGHTS.filter((s) => s.step <= maxStep);
  const total = filtered.reduce((acc, s) => acc + s.weight, 0);
  let r = rng.next() * total;
  for (const s of filtered) {
    r -= s.weight;
    if (r <= 0) return s.step;
  }
  return filtered[filtered.length - 1]!.step;
}

/* ─── Audio rendering ────────────────────────────────────────────────── */

function toMidiInReferenceOctave(sd: ScaleDegree, key: KeyChoice): number {
  const pattern = MODE_PATTERNS[normalizeMode(key.mode)];
  const baseOffset = pattern[((sd.degree - 1) % 7 + 7) % 7]!;
  const altShift = sd.alteration === "♯" ? 1 : sd.alteration === "♭" ? -1 : 0;
  const octave = sd.octaveOffset ?? 0;
  return key.tonicMidi + 12 + baseOffset + altShift + octave * 12;
}

/**
 * Wrong-answer teach plan: replay the target melody in slow motion so
 * the user can re-examine the contour and intervals at a comfortable
 * tempo. No cadence — by the time the teach plan plays, the user
 * already heard the cadence; spending another four bars on it before
 * the corrected playback would erode the "this is what you missed"
 * focus.
 */
function buildDictationTeachPlan(sequence: ScaleDegreeSequence, key: KeyChoice): AudioPlan {
  const builder = new AudioPlanBuilder();
  for (const sd of sequence) {
    const midi = toMidiInReferenceOctave(sd, key);
    builder.addNote(midi, TEACH_NOTE_DURATION, 0.22, "melody");
    builder.advance(TEACH_NOTE_DURATION + TEACH_NOTE_GAP);
  }
  // A final tonic for grounding so the slow playback resolves cleanly.
  builder.addNote(key.tonicMidi + 12, TEACH_NOTE_DURATION, 0.22, "target");
  builder.advance(TEACH_NOTE_DURATION + 0.3);
  return builder.build();
}

const MAJOR_SOLFEGE = ["do", "re", "mi", "fa", "sol", "la", "ti"] as const;
const CHROMATIC_RAISED = ["di", "ri", "fi", "si", "li"] as const;
const CHROMATIC_LOWERED = ["ra", "me", "se", "le", "te"] as const;

function syllableFor(sd: ScaleDegree): string {
  const base = MAJOR_SOLFEGE[((sd.degree - 1) % 7 + 7) % 7]!;
  if (!sd.alteration) return base;
  // Indices in the chromatic syllable arrays:
  //   raised: di(1), ri(2), fi(4), si(5), li(6)
  //   lowered: ra(2), me(3), se(5), le(6), te(7)
  if (sd.alteration === "♯") {
    const idx: Record<number, number> = { 1: 0, 2: 1, 4: 2, 5: 3, 6: 4 };
    const i = idx[sd.degree];
    return i !== undefined ? CHROMATIC_RAISED[i]! : base;
  }
  const idxL: Record<number, number> = { 2: 0, 3: 1, 5: 2, 6: 3, 7: 4 };
  const i = idxL[sd.degree];
  return i !== undefined ? CHROMATIC_LOWERED[i]! : base;
}

function buildExplanation(sequence: ScaleDegreeSequence, key: KeyChoice): string {
  const syllables = sequence.map(syllableFor).join(" ");
  return `${syllables} in ${key.name}.`;
}
